/**
 * ActivityHeatmap — weekday × ISO-week grid of session counts. Companion
 * to the Sessions per day bar chart in DemoExperience: the bar chart
 * shows the linear time series, the heatmap surfaces day-of-week and
 * week-over-week patterns that the bars don't expose.
 *
 * Cell fill: accent at opacity proportional to session count vs the
 * window max. Empty days render as a faint `soft` swatch so the grid
 * stays legible even when the device is dormant. Hover reveals a styled
 * tooltip (matching the timeline's vocabulary); click drills to the
 * sessions-for-day route when an onCellClick is provided.
 */
import { useMemo, useRef, useState } from 'react';
import type { SessionDay } from '../api/hooks';
import { fmtDuration } from './format';

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function utcDateOf(iso: string): Date {
  // Backend day strings come in as "2026-05-25T00:00:00.000Z" — parse
  // and reduce to UTC midnight to avoid local-tz drift moving the cell.
  const d = new Date(iso);
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()),
  );
}

function mondayOf(d: Date): Date {
  // ISO weeks start on Monday. Returns a UTC-midnight Date of the
  // Monday of the week containing `d`.
  const out = new Date(d.valueOf());
  const dayNr = (out.getUTCDay() + 6) % 7; // 0 = Mon … 6 = Sun
  out.setUTCDate(out.getUTCDate() - dayNr);
  return out;
}

function fmtCellTitle(d: Date): string {
  return d.toLocaleDateString([], {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

interface Cell {
  weekKey: string;
  weekStart: Date;
  weekday: number; // 0 = Mon
  date: Date;
  data: SessionDay | null;
}

export function ActivityHeatmap({
  days,
  onCellClick,
}: {
  /** Current-window session days, ASC or DESC — order doesn't matter. */
  days: SessionDay[];
  /** Drill handler: receives the day's YYYY-MM-DD string. */
  onCellClick?: (yyyymmdd: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [hover, setHover] = useState<{
    cell: Cell;
    xPx: number;
    yPx: number;
  } | null>(null);

  const { cells, weeks, max } = useMemo(() => {
    if (days.length === 0) {
      return { cells: [] as Cell[], weeks: [] as string[], max: 0 };
    }
    // Build a lookup keyed by YYYY-MM-DD for O(1) per-cell access.
    const byKey = new Map<string, SessionDay>();
    let maxSessions = 0;
    let earliest = utcDateOf(days[0]!.day);
    let latest = earliest;
    for (const r of days) {
      const d = utcDateOf(r.day);
      const key = d.toISOString().slice(0, 10);
      byKey.set(key, r);
      if (r.sessions > maxSessions) maxSessions = r.sessions;
      if (d < earliest) earliest = d;
      if (d > latest) latest = d;
    }
    // Walk Monday-by-Monday from the earliest week to the latest, filling
    // every (week, weekday) cell — empty days render as a faint swatch.
    const firstMonday = mondayOf(earliest);
    const lastMonday = mondayOf(latest);
    const acc: Cell[] = [];
    const weekKeys: string[] = [];
    let cursor = new Date(firstMonday.valueOf());
    while (cursor <= lastMonday) {
      const weekKey = cursor.toISOString().slice(0, 10);
      weekKeys.push(weekKey);
      for (let w = 0; w < 7; w++) {
        const cellDate = new Date(cursor.valueOf());
        cellDate.setUTCDate(cellDate.getUTCDate() + w);
        const cellKey = cellDate.toISOString().slice(0, 10);
        acc.push({
          weekKey,
          weekStart: new Date(cursor.valueOf()),
          weekday: w,
          date: cellDate,
          data: byKey.get(cellKey) ?? null,
        });
      }
      cursor.setUTCDate(cursor.getUTCDate() + 7);
    }
    return { cells: acc, weeks: weekKeys, max: maxSessions };
  }, [days]);

  if (cells.length === 0) {
    return <p className="text-xs text-muted">No data in this window.</p>;
  }

  // SVG geometry — cells are square at the chart's native cell-size; SVG
  // viewBox stretches horizontally to fill the container width.
  const CELL = 14; // px
  const GAP = 2;
  const LABEL_W = 26;
  const MONTH_LABEL_H = 12;
  const gridW = LABEL_W + weeks.length * (CELL + GAP);
  const gridH = MONTH_LABEL_H + 7 * (CELL + GAP);

  // Month labels above the grid, one per first week of each month.
  const monthLabels: Array<{ x: number; label: string }> = [];
  let lastMonth = -1;
  weeks.forEach((wk, i) => {
    const d = new Date(wk);
    const m = d.getUTCMonth();
    if (m !== lastMonth) {
      monthLabels.push({
        x: LABEL_W + i * (CELL + GAP),
        label: d.toLocaleDateString([], { month: 'short' }),
      });
      lastMonth = m;
    }
  });

  return (
    <div ref={containerRef} className="relative" style={{ height: gridH }}>
      <svg
        viewBox={`0 0 ${gridW} ${gridH}`}
        preserveAspectRatio="xMinYMid meet"
        className="block"
        style={{ height: gridH, maxWidth: '100%' }}
      >
        {/* Month labels */}
        {monthLabels.map((m, i) => (
          <text
            key={`m-${i}`}
            x={m.x}
            y={MONTH_LABEL_H - 2}
            fontSize={9}
            className="fill-muted"
            style={{ fontFamily: 'inherit' }}
          >
            {m.label}
          </text>
        ))}

        {/* Weekday labels (Mon / Wed / Fri to avoid clutter) */}
        {[0, 2, 4].map((w) => (
          <text
            key={`wd-${w}`}
            x={0}
            y={MONTH_LABEL_H + w * (CELL + GAP) + CELL - 3}
            fontSize={9}
            className="fill-muted"
            style={{ fontFamily: 'inherit' }}
          >
            {WEEKDAY_LABELS[w]}
          </text>
        ))}

        {/* Cells */}
        {cells.map((c) => {
          const colIdx = weeks.indexOf(c.weekKey);
          const x = LABEL_W + colIdx * (CELL + GAP);
          const y = MONTH_LABEL_H + c.weekday * (CELL + GAP);
          const sessions = c.data?.sessions ?? 0;
          const opacity =
            sessions === 0 ? 0 : 0.12 + (sessions / Math.max(max, 1)) * 0.88;
          const empty = sessions === 0;
          return (
            <rect
              key={`${c.weekKey}-${c.weekday}`}
              x={x}
              y={y}
              width={CELL}
              height={CELL}
              rx={2}
              fill={empty ? '#EBEBEB' : '#00B4D0'}
              fillOpacity={empty ? 1 : opacity}
              className={onCellClick && c.data ? 'cursor-pointer' : ''}
              onMouseEnter={(e) => {
                const rect = containerRef.current?.getBoundingClientRect();
                if (!rect) return;
                setHover({
                  cell: c,
                  xPx: e.clientX - rect.left,
                  yPx: e.clientY - rect.top,
                });
              }}
              onMouseMove={(e) => {
                const rect = containerRef.current?.getBoundingClientRect();
                if (!rect) return;
                setHover({
                  cell: c,
                  xPx: e.clientX - rect.left,
                  yPx: e.clientY - rect.top,
                });
              }}
              onMouseLeave={() => setHover(null)}
              onClick={
                onCellClick && c.data
                  ? () => onCellClick(c.date.toISOString().slice(0, 10))
                  : undefined
              }
            />
          );
        })}
      </svg>

      {/* Styled tooltip — same vocabulary as the bar chart's and the
          state timeline's. */}
      {hover && (
        <div
          role="tooltip"
          className="absolute z-10 -translate-x-1/2 -translate-y-full bg-white border border-border rounded-md shadow-lg px-2.5 py-1.5 text-[10px] text-fg tabular-nums whitespace-nowrap pointer-events-none"
          style={{
            left: (() => {
              const cw = containerRef.current?.clientWidth ?? 0;
              const halfW = 90;
              return Math.max(halfW, Math.min(cw - halfW, hover.xPx));
            })(),
            top: hover.yPx - 8,
          }}
        >
          <div className="font-medium mb-1">{fmtCellTitle(hover.cell.date)}</div>
          {hover.cell.data ? (
            <>
              <div className="flex gap-3">
                <span className="text-muted">Sessions</span>
                <span className="ml-auto">{hover.cell.data.sessions}</span>
              </div>
              <div className="flex gap-3">
                <span className="text-muted">Active</span>
                <span className="ml-auto">
                  {fmtDuration(Number(hover.cell.data.total_active_s))}
                </span>
              </div>
              <div className="flex gap-3">
                <span className="text-muted">Feedback</span>
                <span className="ml-auto">{hover.cell.data.feedback_count}</span>
              </div>
            </>
          ) : (
            <div className="text-muted italic">No sessions</div>
          )}
        </div>
      )}

      {/* Legend — small scale strip under the grid. */}
      <div className="flex items-center gap-1 mt-2 text-[10px] text-muted">
        <span>Less</span>
        {[0.12, 0.32, 0.52, 0.72, 1].map((op, i) => (
          <span
            key={i}
            className="inline-block rounded-sm"
            style={{ width: 12, height: 12, backgroundColor: '#00B4D0', opacity: op }}
          />
        ))}
        <span>More</span>
        <span className="ml-3">
          window max: <span className="tabular-nums font-mono">{max}</span>
        </span>
      </div>
    </div>
  );
}
